import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useRef } from 'react'
import useGlobalStore from '@/store/useGlobalStore'

const generateUniqueUserId = (): string => {
  const storageKey = 'ld_user_id'

  if (typeof window === 'undefined') {
    return 'ssr-user'
  }

  let userId = localStorage.getItem(storageKey)

  if (!userId) {
    userId = crypto.randomUUID()
    localStorage.setItem(storageKey, userId)
  }

  return userId
}

export function useLaunchDarklyContext() {
  const client = useLDClient()

  const storeData = useGlobalStore()
  const uniqueUserId = generateUniqueUserId()

  const customerType = storeData?.user?.customerType || null
  const alpha2 = storeData?.app?.alpha2 || null
  const launchDarklyDynamicContext =
    storeData?.app?.launchDarklyDynamicContext || null

  const previousValues = useRef({
    alpha2: '',
    customerType: '',
    paymentMethod: null as string | null,
    userId: '',
  })

  const selectedPaymentMethod =
    launchDarklyDynamicContext?.selectedPaymentMethod || null

  const createLaunchDarklyContext = () => ({
    kind: 'multi',
    country: { key: alpha2 || 'unknown' },
    customerType: { key: customerType || 'anonymous' },
    paymentMethod: { key: selectedPaymentMethod || 'none' },
    user: { key: uniqueUserId },
  })

  const hasContextChanged = () => {
    const previous = previousValues.current
    return (
      alpha2 !== previous.alpha2 ||
      customerType !== previous.customerType ||
      selectedPaymentMethod !== previous.paymentMethod ||
      uniqueUserId !== previous.userId
    )
  }

  const updatePreviousValues = () => {
    previousValues.current = {
      alpha2: alpha2 || '',
      customerType: customerType || '',
      paymentMethod: selectedPaymentMethod,
      userId: uniqueUserId,
    }
  }

  const isReadyToUpdate = client && alpha2 && customerType

  useEffect(() => {
    if (!client) return

    try {
      if (isReadyToUpdate && hasContextChanged()) {
        const newContext = createLaunchDarklyContext()

        client.identify(newContext).catch((error) => {
          console.error('Failed to update LaunchDarkly context:', error)
        })

        updatePreviousValues()
      }
    } catch (error) {
      console.error('Error in LaunchDarkly context update:', error)
    }
  }, [client, alpha2, customerType, selectedPaymentMethod])
}
